import React, { useEffect, useState, useMemo } from "react";
import { Routes, Route, Navigate, Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Package, Wallet, ClipboardList, User, LifeBuoy, ShieldCheck,
  CheckCircle2, AlertCircle, XCircle, IndianRupee, QrCode, ArrowLeft, Search,
  Banknote, FileText, Users as UsersIcon, Sparkles, TrendingUp, ChevronRight,
  Download, ArrowUpRight, ArrowDownRight,
} from "lucide-react";
import MerchantLayout from "@/components/MerchantLayout";
import { api, formatError } from "@/lib/api";
import { StatusBadge, ServiceLogo, categoryColor } from "@/components/Brand";
import { MakePaymentBody } from "@/components/MakePayment";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

/* ───────────────────────────────────────── helpers ───────────────────────────────────────── */

function leadFormType(catName = "") {
  const n = (catName || "").toLowerCase();
  if (n.includes("account") && !n.includes("demat")) return "short";
  if (n.includes("credit")) return "short";
  if (n.includes("demat")) return "short";
  return "full";
}

function categoryGradient(name = "") {
  const n = name.toLowerCase();
  if (n.includes("account")) return "linear-gradient(135deg, #047857 0%, #10b981 100%)"; // emerald
  if (n.includes("credit"))  return "linear-gradient(135deg, #c2410c 0%, #f97316 100%)"; // orange
  if (n.includes("loan"))    return "linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)"; // blue
  if (n.includes("insurance")) return "linear-gradient(135deg, #be185d 0%, #ec4899 100%)"; // pink
  if (n.includes("demat"))   return "linear-gradient(135deg, #5b21b6 0%, #8b5cf6 100%)"; // violet
  if (n.includes("bc") || n.includes("csp")) return "linear-gradient(135deg, #0369a1 0%, #0ea5e9 100%)"; // sky
  if (n.includes("gov"))     return "linear-gradient(135deg, #991b1b 0%, #ef4444 100%)"; // rose
  return "linear-gradient(135deg, #334155 0%, #64748b 100%)"; // slate
}

function money(v) { return `₹ ${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }

/* ───────────────────────────────────────── Home ───────────────────────────────────────── */

function Home() {
  const { user, refreshMe } = useAuth();
  const [s, setS] = useState(null);
  const [recent, setRecent] = useState([]);
  const [services, setServices] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/reports/summary").then((r) => setS(r.data)).catch(() => {});
    api.get("/wallet/tx").then((r) => setRecent(r.data.slice(0, 5))).catch(() => {});
    api.get("/services").then((r) => setServices((r.data || []).filter((x) => x.active !== false))).catch(() => {});
    refreshMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeServices = services.length;
  const txCount = (s?.approved || 0) + (s?.pending || 0) + (s?.rejected || 0);
  const greeting = (() => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening"; })();

  return (
    <MerchantLayout hideHeader>
      {/* Wallet Hero */}
      <div className="m-hero p-5 md:p-7 mb-5" data-testid="m-home-hero">
        <div className="relative z-10">
          <div className="text-sm opacity-80 font-medium">{greeting},</div>
          <div className="text-xl md:text-2xl font-extrabold tracking-tight">{user?.name || "Merchant"} 👋</div>

          <div className="mt-6 flex items-end justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs opacity-80 uppercase tracking-widest font-semibold">Wallet Balance</div>
              <div className="text-4xl md:text-5xl font-extrabold tracking-tight mt-1" data-testid="m-home-balance">{money(user?.wallet_balance)}</div>
              {Number(user?.wallet_locked || 0) > 0 && (
                <div className="text-xs opacity-75 mt-1">+ Locked: {money(user?.wallet_locked)}</div>
              )}
            </div>
            <div className="flex gap-2">
              <Link to="/merchant/make-payment" className="inline-flex items-center gap-1.5 bg-white text-indigo-700 font-bold text-sm rounded-full px-4 py-2.5 shadow-md hover:bg-indigo-50 transition" data-testid="m-home-addmoney">
                <ArrowDownRight className="h-4 w-4" /> Add Money
              </Link>
              <Link to="/merchant/wallet" className="inline-flex items-center gap-1.5 bg-white/15 text-white font-bold text-sm rounded-full px-4 py-2.5 border border-white/25 hover:bg-white/25 transition" data-testid="m-home-withdraw">
                <ArrowUpRight className="h-4 w-4" /> Withdraw
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* KYC alert */}
      {user?.kyc_status !== "approved" && (
        <Link to="/merchant/kyc" className="block mb-5 rounded-2xl p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 hover:shadow-md transition" data-testid="m-home-kyc-alert">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-amber-500/15 text-amber-700 flex items-center justify-center"><ShieldCheck className="h-5 w-5" /></div>
            <div className="flex-1">
              <div className="font-bold text-amber-900 text-sm">KYC {user?.kyc_status === "pending" ? "review में है" : "pending है"}</div>
              <div className="text-xs text-amber-800/80 mt-0.5">Full access के लिए KYC complete करें</div>
            </div>
            <ChevronRight className="h-5 w-5 text-amber-700" />
          </div>
        </Link>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <StatCard testId="m-stat-txn"      icon={TrendingUp}   label="Transactions"    value={txCount} accent="indigo" />
        <StatCard testId="m-stat-approved" icon={CheckCircle2} label="Approved"        value={s?.approved ?? 0} accent="emerald" />
        <StatCard testId="m-stat-services" icon={Package}      label="Active Services" value={activeServices} accent="violet" />
      </div>

      {/* Quick actions */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="font-bold text-slate-900">Quick Actions</div>
          <Sparkles className="h-4 w-4 text-indigo-500" />
        </div>
        <div className="grid grid-cols-4 gap-3">
          {[
            { to: "/merchant/make-payment", icon: QrCode,        label: "Scan & Pay", bg: "from-indigo-500 to-purple-600" },
            { to: "/merchant/services",     icon: Package,       label: "Services",   bg: "from-emerald-500 to-teal-600" },
            { to: "/merchant/leads",        icon: ClipboardList, label: "My Leads",   bg: "from-orange-500 to-rose-600" },
            { to: "/merchant/wallet",       icon: Banknote,      label: "Wallet",     bg: "from-sky-500 to-blue-600" },
          ].map((a) => (
            <Link to={a.to} key={a.to} className="m-action-tile p-3 flex flex-col items-center justify-center gap-2 text-center" data-testid={`m-quick-${a.label.toLowerCase().replace(/\s|&/g, "-").replace(/-+/g, "-")}`}>
              <div className={`h-12 w-12 rounded-2xl bg-gradient-to-br ${a.bg} text-white flex items-center justify-center shadow-md`}>
                <a.icon className="h-6 w-6" strokeWidth={2.2} />
              </div>
              <div className="text-[11px] font-semibold text-slate-700 leading-tight">{a.label}</div>
            </Link>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="font-bold text-slate-900">Recent Activity</div>
          <Link to="/merchant/wallet" className="text-xs font-semibold text-indigo-600 flex items-center gap-1">View all <ChevronRight className="h-3 w-3" /></Link>
        </div>
        <div className="bg-white rounded-2xl border border-indigo-100/60 overflow-hidden">
          {recent.length === 0 && <div className="p-6 text-center text-sm text-slate-500">कोई transaction नहीं है</div>}
          {recent.map((t, i) => (
            <div key={t.id} className={`px-4 py-3 flex items-center gap-3 ${i < recent.length - 1 ? "border-b border-slate-100" : ""}`} data-testid={`m-recent-${t.id}`}>
              <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${t.type === "credit" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                {t.type === "credit" ? <ArrowDownRight className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">{t.note || (t.type === "credit" ? "Credit" : "Debit")}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{(t.created_at || "").replace("T", " ").slice(0, 16)}</div>
              </div>
              <div className={`font-bold text-sm ${t.type === "credit" ? "text-emerald-700" : "text-rose-700"}`}>
                {t.type === "credit" ? "+" : "−"} ₹{Number(t.amount).toFixed(2)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </MerchantLayout>
  );
}

function StatCard({ icon: Icon, label, value, accent = "indigo", testId }) {
  const map = {
    indigo:  "from-indigo-500 to-indigo-700",
    emerald: "from-emerald-500 to-emerald-700",
    violet:  "from-violet-500 to-violet-700",
    orange:  "from-orange-500 to-orange-700",
    rose:    "from-rose-500 to-rose-700",
  };
  return (
    <div className="bg-white rounded-2xl p-3.5 border border-indigo-100/60 shadow-sm" data-testid={testId}>
      <div className={`h-9 w-9 rounded-xl bg-gradient-to-br ${map[accent]} text-white flex items-center justify-center mb-2`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">{label}</div>
      <div className="font-extrabold text-xl text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}

/* ───────────────────────────── Services (Two-Level Navigation) ───────────────────────────── */

function ServicesPage() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [activeCat, setActiveCat] = useState(null); // category object OR null (level 1)
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ customer_name: "", customer_mobile: "", customer_email: "", aadhaar_no: "", pan_no: "", address: "", state: "", district: "", pincode: "", notes: "" });

  useEffect(() => {
    api.get("/services").then((r) => setItems(r.data.filter((s) => s.active !== false)));
    api.get("/categories").then((r) => setCats(r.data));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leads", { service_id: active.id, ...form });
      toast.success("Lead submit हो गया");
      setOpen(false);
      setForm({ customer_name: "", customer_mobile: "", customer_email: "", aadhaar_no: "", pan_no: "", address: "", state: "", district: "", pincode: "", notes: "" });
    } catch (err) { toast.error(formatError(err)); }
  };

  // Group categories with counts
  const catsWithCounts = useMemo(() => {
    return cats
      .map((c) => ({ ...c, items: items.filter((s) => s.category_id === c.id) }))
      .filter((g) => g.items.length > 0);
  }, [cats, items]);

  // Level 2: services of active category
  const catServices = useMemo(() => {
    if (!activeCat) return [];
    const list = items.filter((s) => s.category_id === activeCat.id);
    if (!search.trim()) return list;
    const q = search.trim().toLowerCase();
    return list.filter((s) => (s.name || "").toLowerCase().includes(q) || (s.description || "").toLowerCase().includes(q));
  }, [items, activeCat, search]);

  const formType = leadFormType(activeCat?.name);

  // ---------- Level 1 (Category tiles) ----------
  if (!activeCat) {
    return (
      <MerchantLayout title="Services" subtitle="Category चुनें और services देखें">
        {catsWithCounts.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center text-slate-500">
            अभी कोई services available नहीं हैं.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {catsWithCounts.map((c) => (
              <button
                key={c.id}
                onClick={() => { setActiveCat(c); setSearch(""); }}
                className="m-cat-tile text-left"
                style={{ background: categoryGradient(c.name) }}
                data-testid={`m-cat-${c.id}`}
              >
                <div className="relative z-10">
                  <div className="h-12 w-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <Package className="h-6 w-6 text-white" strokeWidth={2.2} />
                  </div>
                </div>
                <div className="relative z-10">
                  <div className="font-bold text-base md:text-lg leading-tight">{c.name}</div>
                  <div className="text-xs opacity-90 mt-0.5">{c.items.length} {c.items.length === 1 ? "service" : "services"}</div>
                  <div className="mt-2 inline-flex items-center gap-1 text-xs font-semibold">
                    Explore <ChevronRight className="h-3.5 w-3.5" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </MerchantLayout>
    );
  }

  // ---------- Level 2 (Services within category) ----------
  const cc = categoryColor(activeCat.name);
  return (
    <MerchantLayout hideHeader>
      {/* Breadcrumb header */}
      <div className="mb-4 flex items-center gap-3">
        <button
          onClick={() => { setActiveCat(null); setSearch(""); }}
          className="m-tap h-10 w-10 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:bg-slate-50 shadow-sm"
          data-testid="m-svc-back"
          aria-label="Back to categories"
        >
          <ArrowLeft className="h-5 w-5 text-slate-700" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Category</div>
          <div className="font-extrabold text-xl text-slate-900 tracking-tight truncate">{activeCat.name}</div>
        </div>
        <div className="hidden md:block">
          <div className={`h-10 w-10 rounded-2xl shadow-md`} style={{ background: categoryGradient(activeCat.name) }} />
        </div>
      </div>

      {/* Search bar */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          type="text"
          placeholder={`${activeCat.name} में search करें…`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 rounded-2xl bg-white border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
          data-testid="m-svc-search"
        />
      </div>

      {/* Services list */}
      {catServices.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center text-slate-500">
          {search ? "Search से कोई service match नहीं हुई" : "इस category में कोई service नहीं है"}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {catServices.map((s) => (
            <div key={s.id} className="m-action-tile p-4 relative overflow-hidden" data-testid={`m-svc-${s.id}`}>
              <div className={`absolute -top-10 -right-10 w-28 h-28 rounded-full ${cc.grad} opacity-10`} />
              <div className="flex items-start gap-3 relative">
                <ServiceLogo url={s.logo_url} name={s.name} size={48} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-bold text-slate-900 text-sm truncate">{s.name}</div>
                    {s.charge_type === "paid"
                      ? <span className="chip border bg-orange-50 text-orange-700 border-orange-200 whitespace-nowrap">₹{Number(s.charge_amount || 0).toFixed(0)}</span>
                      : <span className="chip border bg-emerald-50 text-emerald-700 border-emerald-200 whitespace-nowrap">FREE</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-1 line-clamp-2">{s.description || "—"}</div>
                  {Number(s.merchant_commission || 0) > 0 && (
                    <div className="text-xs text-emerald-700 mt-1 font-semibold">Commission: {money(s.merchant_commission)}</div>
                  )}
                </div>
              </div>
              <div className="mt-3 flex gap-2 flex-wrap relative">
                {(s.service_type === "open_link" || s.service_type === "link_and_form") && s.open_link && (
                  <a href={s.open_link} target="_blank" rel="noreferrer">
                    <Button variant="outline" size="sm" className="rounded-full" data-testid={`m-svc-link-${s.id}`}>Open Link ↗</Button>
                  </a>
                )}
                {(s.service_type === "lead_form" || s.service_type === "link_and_form") && (
                  <Button size="sm" className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:opacity-90 text-white rounded-full" onClick={() => { setActive(s); setOpen(true); }} data-testid={`m-svc-form-${s.id}`}>Submit Lead</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{active?.name} — Lead Submit करें</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label>Customer Name / नाम *</Label><Input required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="m-lead-name" /></div>
            <div><Label>Mobile No / मोबाइल *</Label><Input required inputMode="numeric" value={form.customer_mobile} onChange={(e) => setForm({ ...form, customer_mobile: e.target.value })} data-testid="m-lead-mobile" /></div>
            <div className="md:col-span-2"><Label>Gmail ID / ईमेल</Label><Input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} data-testid="m-lead-email" /></div>
            <div className="md:col-span-2"><Label>PAN Card No / पैन</Label><Input value={form.pan_no} onChange={(e) => setForm({ ...form, pan_no: e.target.value.toUpperCase() })} data-testid="m-lead-pan" /></div>
            {formType === "full" && (
              <>
                <div><Label>Aadhaar Card No / आधार</Label><Input inputMode="numeric" value={form.aadhaar_no} onChange={(e) => setForm({ ...form, aadhaar_no: e.target.value })} data-testid="m-lead-aadhaar" /></div>
                <div><Label>Pin Code / पिन कोड</Label><Input inputMode="numeric" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} data-testid="m-lead-pincode" /></div>
                <div className="md:col-span-2"><Label>Full Address / पूरा पता</Label><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="m-lead-address" /></div>
                <div><Label>State / राज्य</Label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="m-lead-state" /></div>
                <div><Label>District / जिला</Label><Input value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} data-testid="m-lead-district" /></div>
                <div className="md:col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="m-lead-notes" /></div>
              </>
            )}
            <DialogFooter className="md:col-span-2"><Button type="submit" className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-full px-6" data-testid="m-lead-submit">Submit Lead</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </MerchantLayout>
  );
}

/* ───────────────────────────────────── Leads ───────────────────────────────────── */

function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [svcs, setSvcs] = useState([]);
  useEffect(() => { api.get("/leads").then((r) => setLeads(r.data)); api.get("/services").then((r) => setSvcs(r.data)); }, []);
  return (
    <MerchantLayout title="My Leads" subtitle="आपके submit किए leads एक जगह">
      <div className="bg-white border border-indigo-100/60 rounded-2xl overflow-hidden">
        {/* Mobile cards */}
        <div className="md:hidden">
          {leads.length === 0 && <div className="p-8 text-center text-slate-500 text-sm">No leads yet</div>}
          {leads.map((l) => { const s = svcs.find((x) => x.id === l.service_id); return (
            <div key={l.id} className="px-4 py-3 border-b last:border-b-0 border-slate-100" data-testid={`m-lead-row-${l.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-bold text-slate-900 text-sm truncate">{l.customer_name}</div>
                  <div className="text-xs text-slate-500 font-mono">{l.customer_mobile}</div>
                  <div className="text-xs text-indigo-600 font-medium mt-0.5">{s?.name || l.service_id}</div>
                </div>
                <StatusBadge status={l.status} />
              </div>
              <div className="text-[10px] text-slate-400 mt-1">{(l.created_at || "").split("T")[0]}</div>
            </div>
          ); })}
        </div>
        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Customer</TableHead><TableHead>Mobile</TableHead><TableHead>Service</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
            <TableBody>
              {leads.map((l) => { const s = svcs.find((x) => x.id === l.service_id); return (
                <TableRow key={l.id}><TableCell className="font-medium">{l.customer_name}</TableCell><TableCell className="font-mono text-sm">{l.customer_mobile}</TableCell><TableCell>{s?.name || l.service_id}</TableCell><TableCell><StatusBadge status={l.status} /></TableCell><TableCell className="text-xs text-slate-500">{(l.created_at || "").split("T")[0]}</TableCell></TableRow>
              ); })}
              {leads.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">No leads yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>
    </MerchantLayout>
  );
}

/* ───────────────────────────────────── Wallet ───────────────────────────────────── */

function WalletPage() {
  const { user, refreshMe } = useAuth();
  const [txs, setTxs] = useState([]);
  const [wr, setWr] = useState([]);
  const [wForm, setWForm] = useState({ amount: "", method: "bank", account_details: "" });
  const [showWithdraw, setShowWithdraw] = useState(false);
  const load = async () => { setTxs((await api.get("/wallet/tx")).data); setWr((await api.get("/withdrawals")).data); await refreshMe(); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const req = async (e) => {
    e.preventDefault();
    try { await api.post("/withdrawals", { ...wForm, amount: Number(wForm.amount) }); toast.success("Withdrawal requested"); setWForm({ amount: "", method: "bank", account_details: "" }); setShowWithdraw(false); load(); }
    catch (err) { toast.error(formatError(err)); }
  };
  return (
    <MerchantLayout hideHeader>
      {/* Wallet hero */}
      <div className="m-hero p-5 md:p-7 mb-5">
        <div className="relative z-10">
          <div className="text-xs opacity-80 uppercase tracking-widest font-semibold">Available Balance</div>
          <div className="text-4xl md:text-5xl font-extrabold tracking-tight mt-1" data-testid="m-wal-bal">{money(user?.wallet_balance)}</div>
          {Number(user?.wallet_locked || 0) > 0 && <div className="text-xs opacity-75 mt-1">Locked: {money(user?.wallet_locked)}</div>}
          <div className="mt-5 flex gap-2 flex-wrap">
            <Link to="/merchant/make-payment" className="inline-flex items-center gap-1.5 bg-white text-indigo-700 font-bold text-sm rounded-full px-4 py-2.5 shadow-md hover:bg-indigo-50 transition" data-testid="m-wal-addmoney">
              <ArrowDownRight className="h-4 w-4" /> Add Money
            </Link>
            <button onClick={() => setShowWithdraw(true)} className="inline-flex items-center gap-1.5 bg-white/15 text-white font-bold text-sm rounded-full px-4 py-2.5 border border-white/25 hover:bg-white/25 transition" data-testid="m-wal-withdraw">
              <ArrowUpRight className="h-4 w-4" /> Withdraw
            </button>
          </div>
        </div>
      </div>

      {/* Transactions */}
      <div className="mb-5">
        <div className="font-bold text-slate-900 mb-3">Transactions</div>
        <div className="bg-white rounded-2xl border border-indigo-100/60 overflow-hidden">
          {txs.length === 0 && <div className="p-6 text-center text-sm text-slate-500">No transactions</div>}
          {txs.map((t, i) => (
            <div key={t.id} className={`px-4 py-3 flex items-center gap-3 ${i < txs.length - 1 ? "border-b border-slate-100" : ""}`}>
              <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${t.type === "credit" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                {t.type === "credit" ? <ArrowDownRight className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">{t.note || (t.type === "credit" ? "Credit" : "Debit")}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{(t.created_at || "").replace("T", " ").slice(0, 16)}</div>
              </div>
              <div className={`font-bold text-sm ${t.type === "credit" ? "text-emerald-700" : "text-rose-700"}`}>
                {t.type === "credit" ? "+" : "−"} ₹{Number(t.amount).toFixed(2)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Withdrawal Requests */}
      <div className="mb-5">
        <div className="font-bold text-slate-900 mb-3">Withdrawal Requests</div>
        <div className="bg-white rounded-2xl border border-indigo-100/60 overflow-hidden">
          {wr.length === 0 && <div className="p-6 text-center text-sm text-slate-500">No requests</div>}
          {wr.map((w, i) => (
            <div key={w.id} className={`px-4 py-3 flex items-center gap-3 ${i < wr.length - 1 ? "border-b border-slate-100" : ""}`}>
              <div className="h-10 w-10 rounded-xl bg-orange-100 text-orange-700 flex items-center justify-center"><Download className="h-4 w-4" /></div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900">{money(w.amount)}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{w.method} · {(w.created_at || "").split("T")[0]}</div>
              </div>
              <StatusBadge status={w.status === "pending" ? "pending" : w.status === "approved" ? "approved" : "rejected"} />
            </div>
          ))}
        </div>
      </div>

      {/* Withdraw dialog */}
      <Dialog open={showWithdraw} onOpenChange={setShowWithdraw}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Request Withdrawal</DialogTitle></DialogHeader>
          <form onSubmit={req} className="space-y-3">
            <div><Label>Amount *</Label><Input required type="number" step="0.01" value={wForm.amount} onChange={(e) => setWForm({ ...wForm, amount: e.target.value })} data-testid="m-with-amt" /></div>
            <div><Label>Method</Label>
              <Select value={wForm.method} onValueChange={(v) => setWForm({ ...wForm, method: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="bank">Bank Transfer</SelectItem><SelectItem value="upi">UPI</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Account Details *</Label><Input required placeholder="Bank A/c number or UPI ID" value={wForm.account_details} onChange={(e) => setWForm({ ...wForm, account_details: e.target.value })} data-testid="m-with-acc" /></div>
            <DialogFooter><Button type="submit" className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-full px-6" data-testid="m-with-submit">Submit Request</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </MerchantLayout>
  );
}

/* ───────────────────────────────────── KYC / Profile / Support / Make Payment ───────────────────────────────────── */

function KycPage() {
  const { user, refreshMe } = useAuth();
  const [form, setForm] = useState({ pan: "", aadhaar: "", business_type: "", address: "" });
  const [existing, setExisting] = useState(null);
  useEffect(() => { api.get("/kyc").then((r) => setExisting(r.data[0])); }, []);
  useEffect(() => { if (existing) setForm({ pan: existing.pan || "", aadhaar: existing.aadhaar || "", business_type: existing.business_type || "", address: existing.address || "" }); }, [existing]);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/kyc", form); toast.success("KYC submitted"); await refreshMe(); const r = await api.get("/kyc"); setExisting(r.data[0]); }
    catch (err) { toast.error(formatError(err)); }
  };
  return (
    <MerchantLayout title="KYC" subtitle="Manual KYC — details भरें">
      <div className="bg-white border border-indigo-100/60 rounded-2xl p-5 md:p-6 max-w-2xl shadow-sm">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Current Status</div>
          <StatusBadge status={user?.kyc_status || "not_submitted"} />
        </div>
        <form onSubmit={submit} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>PAN</Label><Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} data-testid="kyc-pan" /></div>
          <div><Label>Aadhaar</Label><Input value={form.aadhaar} onChange={(e) => setForm({ ...form, aadhaar: e.target.value })} data-testid="kyc-aadhaar" /></div>
          <div className="md:col-span-2"><Label>Business Type</Label>
            <Select value={form.business_type} onValueChange={(v) => setForm({ ...form, business_type: v })}>
              <SelectTrigger data-testid="kyc-btype"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="individual">Individual</SelectItem>
                <SelectItem value="proprietorship">Proprietorship</SelectItem>
                <SelectItem value="partnership">Partnership</SelectItem>
                <SelectItem value="pvt_ltd">Private Limited</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2"><Label>Address</Label><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="kyc-address" /></div>
          <div className="md:col-span-2 flex justify-end"><Button type="submit" className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-full px-6" data-testid="kyc-submit">Submit for Review</Button></div>
        </form>
      </div>
    </MerchantLayout>
  );
}

function ProfilePage() {
  const { user, refreshMe } = useAuth();
  const [form, setForm] = useState({});
  const [uploading, setUploading] = useState(false);
  const [pwd, setPwd] = useState({ old_password: "", new_password: "" });
  const fileRef = React.useRef(null);
  useEffect(() => {
    if (user) setForm({
      name: user.name || "", email: user.email || "", business_name: user.business_name || "",
      photo_url: user.photo_url || "",
      aadhaar_no: user.aadhaar_no || "", pan_no: user.pan_no || "",
      address: user.address || "", state: user.state || "", district: user.district || "", pincode: user.pincode || "",
      bank_name: user.bank_name || "", branch_name: user.branch_name || "", account_number: user.account_number || "", ifsc_code: user.ifsc_code || "",
    });
  }, [user]);

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Only image files");
    if (file.size > 2 * 1024 * 1024) return toast.error("Max 2 MB");
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const absUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      await api.patch("/users/me/profile", { photo_url: absUrl });
      setForm((f) => ({ ...f, photo_url: absUrl }));
      await refreshMe();
      toast.success("Photo updated");
    } catch (err) { toast.error(formatError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async (e) => {
    e.preventDefault();
    try { await api.patch("/users/me/profile", form); toast.success("Profile updated"); await refreshMe(); }
    catch (err) { toast.error(formatError(err)); }
  };

  const changePwd = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/change-password", pwd); toast.success("Password changed"); setPwd({ old_password: "", new_password: "" }); }
    catch (err) { toast.error(formatError(err)); }
  };

  return (
    <MerchantLayout title="Profile" subtitle="Personal & bank details manage करें">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 bg-white border border-indigo-100/60 rounded-2xl p-6 flex flex-col items-center text-center shadow-sm">
          <div className="relative">
            {form.photo_url ? (
              <img src={form.photo_url} alt="me" className="w-28 h-28 rounded-full object-cover border-4 border-white shadow-lg" />
            ) : (
              <div className="w-28 h-28 rounded-full bg-gradient-to-br from-indigo-600 to-purple-600 text-white flex items-center justify-center font-bold text-3xl shadow-lg">
                {(user?.name || "M")[0].toUpperCase()}
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/*" onChange={handlePhoto} className="hidden" data-testid="prof-photo-file" />
            <Button size="sm" variant="outline" className="absolute -bottom-2 left-1/2 -translate-x-1/2 rounded-full text-xs" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="prof-photo-btn">
              {uploading ? "..." : form.photo_url ? "Change" : "Upload"}
            </Button>
          </div>
          <div className="mt-6 font-bold text-lg">{user?.name}</div>
          <div className="text-sm text-slate-500 font-mono">{user?.mobile}</div>
          <div className="mt-2"><StatusBadge status={user?.kyc_status || "not_submitted"} /></div>
        </div>

        <div className="lg:col-span-2 bg-white border border-indigo-100/60 rounded-2xl p-5 md:p-6 shadow-sm">
          <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Personal Details</div>
            <div><Label>Name / नाम</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="prof-name" /></div>
            <div><Label>Gmail ID / ईमेल</Label><Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="prof-email" /></div>
            <div><Label>Business Name / व्यवसाय</Label><Input value={form.business_name || ""} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="prof-business" /></div>
            <div><Label>Aadhaar Card No</Label><Input value={form.aadhaar_no || ""} onChange={(e) => setForm({ ...form, aadhaar_no: e.target.value })} data-testid="prof-aadhaar" /></div>
            <div><Label>PAN Card No</Label><Input value={form.pan_no || ""} onChange={(e) => setForm({ ...form, pan_no: e.target.value.toUpperCase() })} data-testid="prof-pan" /></div>
            <div><Label>Pin Code / पिन कोड</Label><Input value={form.pincode || ""} onChange={(e) => setForm({ ...form, pincode: e.target.value })} data-testid="prof-pincode" /></div>
            <div className="md:col-span-2"><Label>Full Address / पूरा पता</Label><Textarea value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="prof-address" /></div>
            <div><Label>State / राज्य</Label><Input value={form.state || ""} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="prof-state" /></div>
            <div><Label>District / जिला</Label><Input value={form.district || ""} onChange={(e) => setForm({ ...form, district: e.target.value })} data-testid="prof-district" /></div>

            <div className="md:col-span-2 mt-4 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Bank Account Details</div>
            <div><Label>Bank Name</Label><Input value={form.bank_name || ""} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} data-testid="prof-bank" /></div>
            <div><Label>Branch Name</Label><Input value={form.branch_name || ""} onChange={(e) => setForm({ ...form, branch_name: e.target.value })} data-testid="prof-branch" /></div>
            <div><Label>Account Number</Label><Input value={form.account_number || ""} onChange={(e) => setForm({ ...form, account_number: e.target.value })} data-testid="prof-account" /></div>
            <div><Label>IFSC Code</Label><Input value={form.ifsc_code || ""} onChange={(e) => setForm({ ...form, ifsc_code: e.target.value.toUpperCase() })} data-testid="prof-ifsc" /></div>

            <div className="md:col-span-2 flex justify-end"><Button type="submit" className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-full px-6" data-testid="prof-save">Save Profile</Button></div>
          </form>

          <div className="mt-8 pt-6 border-t">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Change Password</div>
            <form onSubmit={changePwd} className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <div><Label>Current Password</Label><Input required type="password" value={pwd.old_password} onChange={(e) => setPwd({ ...pwd, old_password: e.target.value })} data-testid="cp-old" /></div>
              <div><Label>New Password</Label><Input required type="password" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} data-testid="cp-new" /></div>
              <Button type="submit" className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white" data-testid="cp-submit">Update Password</Button>
            </form>
          </div>
        </div>
      </div>
    </MerchantLayout>
  );
}

function SupportPage() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings/support").then((r) => setS(r.data)); }, []);
  return (
    <MerchantLayout title="Support" subtitle="किसी भी query के लिए हमसे संपर्क करें">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
        <a href={`tel:${(s?.customer_care || "").replace(/\s+/g,"")}`} className="m-action-tile p-5 relative overflow-hidden block" data-testid="sup-care-card">
          <div className="absolute -top-10 -right-10 w-28 h-28 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 opacity-10" />
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center text-lg">📞</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-3 font-semibold">Customer Care</div>
          <div className="font-bold text-lg mt-1 text-slate-900" data-testid="sup-care-val">{s?.customer_care || "—"}</div>
        </a>
        <a href={`https://wa.me/${(s?.whatsapp || "").replace(/\D/g,"")}`} target="_blank" rel="noreferrer" className="m-action-tile p-5 relative overflow-hidden block" data-testid="sup-whatsapp-card">
          <div className="absolute -top-10 -right-10 w-28 h-28 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 opacity-10" />
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center text-lg">💬</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-3 font-semibold">WhatsApp</div>
          <div className="font-bold text-lg mt-1 text-slate-900" data-testid="sup-whatsapp-val">{s?.whatsapp || "—"}</div>
        </a>
        <a href={`mailto:${s?.email || ""}`} className="m-action-tile p-5 relative overflow-hidden block" data-testid="sup-email-card">
          <div className="absolute -top-10 -right-10 w-28 h-28 rounded-full bg-gradient-to-br from-orange-500 to-rose-600 opacity-10" />
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-rose-600 text-white flex items-center justify-center text-lg">✉</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-3 font-semibold">Email</div>
          <div className="font-bold text-lg mt-1 text-slate-900 break-all" data-testid="sup-email-val">{s?.email || "—"}</div>
        </a>
        <div className="m-action-tile p-5 relative overflow-hidden" data-testid="sup-office-card">
          <div className="absolute -top-10 -right-10 w-28 h-28 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 opacity-10" />
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 text-white flex items-center justify-center text-lg">📍</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-3 font-semibold">Office Address</div>
          <div className="font-medium mt-1 text-slate-900" data-testid="sup-address-val">{s?.office_address || "—"}</div>
        </div>
      </div>
    </MerchantLayout>
  );
}

function MakePaymentPage() {
  return (
    <MerchantLayout title="Make Payment" subtitle="Admin के active QR पर pay करें और details submit करें">
      <MakePaymentBody />
    </MerchantLayout>
  );
}

export default function MerchantDashboard() {
  return (
    <Routes>
      <Route index element={<Home />} />
      <Route path="services" element={<ServicesPage />} />
      <Route path="leads" element={<LeadsPage />} />
      <Route path="wallet" element={<WalletPage />} />
      <Route path="make-payment" element={<MakePaymentPage />} />
      <Route path="kyc" element={<KycPage />} />
      <Route path="profile" element={<ProfilePage />} />
      <Route path="support" element={<SupportPage />} />
      <Route path="*" element={<Navigate to="/merchant" replace />} />
    </Routes>
  );
}
